// src/store/persist.ts
import type { Middleware } from '@reduxjs/toolkit';

const PERSIST_KEY = 'app-state';
const PERSIST_VERSION = 2 as const;                 // ↑ увеличиваешь при смене формата
const WHITELIST = ['ui', 'members'] as const;       // videos тут не нужно

type Envelope = { __v: number; state: Record<string, unknown> };

function pickWhitelist(state: Record<string, unknown>) {
    const out: Record<string, unknown> = {};
    for (const k of WHITELIST) {
        if (k in state) out[k] = (state as any)[k];
    }
    return out;
}

function hardReset() {
    try { localStorage.removeItem(PERSIST_KEY); } catch {}
}

export function loadPersistedState(): Record<string, unknown> | undefined {
    try {
        const raw = localStorage.getItem(PERSIST_KEY);
        if (!raw) return undefined;

        const parsed = JSON.parse(raw) as Partial<Envelope> | Record<string, unknown>;

        // legacy без версии: мигрируем whitelisted-срез и сохраняем в новом формате
        if (!('__v' in (parsed as any))) {
            const migrated = pickWhitelist(parsed as Record<string, unknown>);
            try {
                const env: Envelope = { __v: PERSIST_VERSION, state: migrated };
                localStorage.setItem(PERSIST_KEY, JSON.stringify(env));
            } catch {}
            return migrated;
        }

        const env = parsed as Partial<Envelope>;
        if (env.__v !== PERSIST_VERSION || typeof env.state !== 'object' || env.state == null) {
            hardReset();
            return undefined;
        }

        // подчистим undefined, чтобы не забивать initialState редьюсеров
        const cleaned: Record<string, unknown> = {};
        for (const k of Object.keys(env.state)) {
            const v = (env.state as any)[k];
            if (v !== undefined) cleaned[k] = v;
        }
        return cleaned;
    } catch {
        hardReset();
        return undefined;
    }
}

export const persistedState = loadPersistedState();

export const persistMiddleware: Middleware = ({ getState }) => (next) => (action) => {
    const result = next(action);
    queueMicrotask(() => {
        try {
            const slice = pickWhitelist(getState() as Record<string, unknown>);
            const env: Envelope = { __v: PERSIST_VERSION, state: slice };
            localStorage.setItem(PERSIST_KEY, JSON.stringify(env));
        } catch {
            // ну не записалось — переживём
        }
    });
    return result;
};
